import React, { useState, useEffect } from 'react';
import { siteConfigApi } from '../utils/api';
import { useLanguageStore } from '../stores/languageStore';
import { t } from '../stores/languageStore';

const AboutUs: React.FC = () => {
  const { language } = useLanguageStore();
  const [configs, setConfigs] = useState<Record<string, string>>({
    about_us_intro: '桃花坛是一个温馨的交流社区，致力于为用户提供一个友好、和谐的交流平台。我们汇聚了来自各地的朋友，共同探讨生活、兴趣和各种话题。',
    about_us_mission: '我们的使命是打造一个温暖的网络家园，让每一位成员都能在这里找到归属感，分享生活点滴，结交志同道合的朋友。',
    about_us_vision: '成为最具温度的社区平台，为用户提供真诚、友善的交流空间，构建一个充满爱与关怀的网络社区。',
    about_us_values: '友善：以友善之心对待每一位成员\n真诚：真诚交流，坦诚相待\n互助：互相帮助，共同成长\n包容：包容不同观点，尊重多样性\n温暖：营造温暖和谐的社区氛围',
    about_us_free_policy: '我们始终坚持面向用户免费的原则，所有核心功能和内容都对用户开放，不收取任何费用。我们的收入来源主要包括：\n\n用户资源捐赠：来自社区成员的自愿捐赠，用于网站的维护和发展\n商务合作广告费：与相关企业的合作广告，为用户提供有价值的产品和服务信息\nGoogle广告：通过Google AdSense等广告服务获取的广告收入\n\n我们承诺将所有收入用于网站的运营、维护和功能升级，确保为用户提供更好的服务和体验。',
    about_us_features: '生活交流与分享\n兴趣话题讨论\n互助问答与支持\n活动组织与参与\n会员互动与交友',
    about_us_team: '我们的团队由一群热爱社区建设的志愿者组成，包括：\n\n社区管理员：负责维护社区秩序，促进良好的交流氛围\n内容编辑：负责整理和发布高质量的内容\n技术支持：负责网站的技术维护和优化\n活动策划：负责组织社区活动和互动',
    about_us_contact: '电子邮件：contact@taohuatan.com\nTelegram：@TaoHuaTan\n官方网站：https://www.taohuatan.com',
    about_us_join: '我们欢迎有才华、有热情的人士加入我们的团队，共同建设这个温暖的社区。如果您对以下角色感兴趣，请联系我们：\n\n社区管理员\n内容创作者\n活动策划\n技术支持\n\n重要说明：\n我们的管理团队是志愿者性质，没有任何工资或报酬。加入我们是基于对社区的热爱和对社区的贡献，不会获取任何收入。',
    about_us_fraud_alert: '尊敬的用户，请注意防范以下诈骗行为：\n\n冒充网站管理员或客服，以各种理由要求您提供账户密码、验证码或支付费用\n声称您的账户存在安全问题，需要进行"验证"或"升级"\n以"中奖"、"优惠活动"等名义诱导您点击链接或提供个人信息\n通过Telegram、微信等渠道发送可疑链接或文件\n要求您向指定账户转账或充值\n\n安全提示：\n\n本网站不会通过电话、短信或邮件要求您提供密码或验证码\n请勿点击来源不明的链接或下载可疑文件\n请勿向任何个人或机构透露您的账户信息和密码\n如遇可疑情况，请通过官方渠道联系我们核实\n定期检查您的账户活动，如发现异常请立即联系我们'
  });
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    const fetchConfigs = async () => {
      setIsLoading(true);
      try {
        const response = await siteConfigApi.getPublicConfigs();
        if (response.success && response.data) {
          const newConfigs: Record<string, string> = { ...configs };
          Object.entries(response.data).forEach(([key, value]) => {
            if (key.startsWith('about_us_')) {
              newConfigs[key] = value as string;
            }
          });
          setConfigs(newConfigs);
        }
      } catch (error) {
        console.error('Failed to fetch about us configs:', error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchConfigs();
  }, []);
  return (
    <div className="bg-white rounded-2xl shadow-sm border border-slate-100 p-6">
      <h1 className="text-2xl font-bold text-slate-900 mb-6">{language === 'en' ? 'About Us' : '关于我们'}</h1>
      
      {isLoading ? (
        <div className="flex justify-center items-center py-10">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
        </div>
      ) : (
        <div className="space-y-6">
          <section className="border-b border-slate-200 pb-6">
            <h2 className="text-xl font-semibold text-red-600 mb-3">{language === 'en' ? 'Fraud Alert' : '防诈骗警示'}</h2>
            <div className="bg-red-50 p-4 rounded-lg border border-red-100">
              {configs.about_us_fraud_alert.split('\n').map((line, index) => (
                <p key={index} className={index === 0 || line.includes('安全提示：') ? "text-slate-600 mb-3 font-medium" : "text-slate-600 mb-1"}>
                  {line}
                </p>
              ))}
            </div>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-slate-800 mb-3">{language === 'en' ? '1. Site Introduction' : '1. 网站简介'}</h2>
            <p className="text-slate-600">
              {configs.about_us_intro}
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-slate-800 mb-3">{language === 'en' ? '2. Our Mission' : '2. 我们的使命'}</h2>
            <p className="text-slate-600">
              {configs.about_us_mission}
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-slate-800 mb-3">{language === 'en' ? '3. Our Vision' : '3. 我们的愿景'}</h2>
            <p className="text-slate-600">
              {configs.about_us_vision}
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-slate-800 mb-3">{language === 'en' ? '4. Core Values' : '4. 核心价值观'}</h2>
            <ul className="list-disc ml-5 text-slate-600 space-y-2">
              {configs.about_us_values.split('\n').map((value, index) => (
                <li key={index}>{value}</li>
              ))}
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-slate-800 mb-3">{language === 'en' ? '5. Free Policy & Revenue Sources' : '5. 免费政策与收入来源'}</h2>
            <div className="space-y-1">
              {configs.about_us_free_policy.split('\n').map((line, index) => (
                <p key={index} className="text-slate-600">{line}</p>
              ))}
            </div>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-slate-800 mb-3">{language === 'en' ? '6. Site Features' : '6. 网站功能'}</h2>
            <ul className="list-disc ml-5 text-slate-600 space-y-2">
              {configs.about_us_features.split('\n').map((feature, index) => (
                <li key={index}>{feature}</li>
              ))}
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-slate-800 mb-3">{language === 'en' ? '7. Team Introduction' : '7. 团队介绍'}</h2>
            <div className="space-y-1">
              {configs.about_us_team.split('\n').map((line, index) => (
                <p key={index} className="text-slate-600">{line}</p>
              ))}
            </div>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-slate-800 mb-3">{language === 'en' ? '8. Contact Us' : '8. 联系我们'}</h2>
            <ul className="list-disc ml-5 text-slate-600 space-y-2">
              {configs.about_us_contact.split('\n').map((contact, index) => (
                <li key={index}>{contact}</li>
              ))}
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-slate-800 mb-3">{language === 'en' ? '9. Join Us' : '9. 加入我们'}</h2>
            <div className="space-y-1">
              {configs.about_us_join.split('\n').map((line, index) => (
                <p key={index} className="text-slate-600">{line}</p>
              ))}
            </div>
          </section>
        </div>
      )}
    </div>
  );
};

export default AboutUs;